/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { loginUser } from "../../redux/users/actions";
import { getToken, getDefaultLandingPath } from "../../utils/storageUtils";
import { useForm } from "react-hook-form";
import logoFull from "../../assets/logo-full.png";

type Inputs = {
  username: string;
  password: string;
};

const Login = () => {
  const [form, setForm] = useState({
    password: "",
    username: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const { register } = useForm<Inputs>();

  const dispatch = useDispatch();
  // Flag posé par LOGIN_USER_REQUEST et retombé par SUCCESS/FAILURE : il
  // couvre exactement la durée de l'appel `POST /users/sign-in`.
  const isSigningIn = useSelector(
    (state: any) => state.usersReducer.loging_user
  );
  const token = getToken();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((form) => ({
      ...form,
      [e.target.name]: e.target.value,
    }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSigningIn) return;
    dispatch(loginUser(form));
  };

  useEffect(() => {
    if (!!token) {
      window.location.replace(getDefaultLandingPath());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const year = new Date().getFullYear();

  return (
    <div className="min-h-screen flex bg-slate-50 font-poppins">
      {/* Panneau gauche — hero vert raffiné, masqué sur mobile.
          Cadre arrondi avec marge externe pour donner l'impression d'une
          carte plutôt qu'un demi-écran. Décor : 3 orbes flous + grille fine
          + grain léger pour casser le flat. */}
      <aside className="hidden md:flex md:w-1/2 lg:w-7/12 relative overflow-hidden m-3 rounded-[28px] bg-gradient-to-br from-green-700 via-green-600 to-emerald-500">
        {/* Orbes décoratifs — couches floutées qui donnent de la profondeur
            au gradient. Volontairement positionnés hors-cadre pour suggérer
            un univers qui dépasse le frame. */}
        <div className="absolute -top-32 -left-24 h-[420px] w-[420px] rounded-full bg-white/15 blur-3xl" />
        <div className="absolute -bottom-32 -right-20 h-[380px] w-[380px] rounded-full bg-emerald-300/30 blur-3xl" />
        <div className="absolute top-1/3 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-yellow-100/15 blur-3xl" />

        {/* Grille fine — légère trame technique en surimpression. */}
        <div
          className="absolute inset-0 opacity-[0.07] mix-blend-overlay"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "36px 36px",
          }}
        />

        {/* Contenu en 3 zones : header / centre / footer. */}
        <div className="relative z-10 flex w-full flex-col justify-between p-10 lg:p-14 text-white">
          {/* Wordmark */}
          <header className="flex items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-700 fill-mode-both">
            <div className="leading-tight">
              <p className="text-[11px] uppercase tracking-[0.32em] text-emerald-50/80 font-medium">
                Compassion
              </p>
              <p className="text-[13px] text-white/85">
                Plateforme médicale · Dr Toho
              </p>
            </div>
          </header>

          {/* Accroche centrale */}
          <div
            className="space-y-7 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-200 fill-mode-both"
          >
            <span
              className="block h-px w-14 bg-white/45"
              aria-hidden="true"
            />
            <h1 className="text-[44px] lg:text-[60px] leading-[1.04] font-semibold tracking-tight">
              Le soin,
              <br />
              <span className="italic font-light text-emerald-50/90">
                avec compassion.
              </span>
            </h1>
            <p className="max-w-md text-[15px] lg:text-base font-light leading-relaxed text-emerald-50/85">
              Un espace dédié aux praticiens, secrétariats et patients pour
              orchestrer chaque consultation avec sérénité.
            </p>
          </div>

          {/* Signature pied de page */}
          <footer className="flex items-center justify-between text-[11px] text-emerald-100/75 animate-in fade-in duration-700 delay-500 fill-mode-both">
            <span>© {year} Compassion · Tous droits réservés</span>
            <span className="flex items-center gap-2">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-200 opacity-80 animate-ping" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-200" />
              </span>
              Service en ligne
            </span>
          </footer>
        </div>
      </aside>

      {/* Panneau droit — formulaire. Conteneur flex pour centrer
          verticalement et horizontalement. */}
      <main className="flex flex-1 items-center justify-center px-6 py-10 sm:px-10 md:py-0">
        <form onSubmit={handleSubmit} className="w-full max-w-sm">
          {/* Logo clinique — affiché sur le panneau formulaire (visible aussi
              sur mobile, où le hero vert est masqué). */}
          <img
            src={logoFull}
            alt="La Compassion — Cabinet d'ophtalmologie"
            className="mb-8 h-20 w-auto block mx-auto animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both"
          />

          {/* Pastille « espace sécurisé » avec dot qui pulse — micro-signal
              de confiance, plus chaleureux qu'un cadenas. */}
          <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50/80 px-3 py-1 text-[11px] font-medium text-emerald-700 animate-in fade-in slide-in-from-top-2 duration-500 fill-mode-both">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
            </span>
            Espace sécurisé
          </div>

          {/* Heading */}
          <div className="mb-9 animate-in fade-in slide-in-from-bottom-2 duration-500 delay-100 fill-mode-both">
            <h2 className="text-4xl font-bold tracking-tight text-slate-900">
              Connexion
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              Heureux de vous revoir. Renseignez vos identifiants.
            </p>
          </div>

          {/* Email */}
          <label className="group block animate-in fade-in slide-in-from-bottom-2 duration-500 delay-200 fill-mode-both">
            <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-slate-500 transition-colors group-focus-within:text-emerald-700">
              Email
            </span>
            <div className="relative">
              <input
                type="text"
                {...register("username", { required: true })}
                onChange={handleChange}
                className="block w-full rounded-xl border border-slate-200 bg-slate-50/70 py-3 pl-11 pr-4 text-sm text-slate-700 placeholder-slate-400 transition-all duration-200 hover:border-slate-300 focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-emerald-500/10"
                placeholder="vous@exemple.com"
              />
              <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-lg text-slate-400 transition-colors group-focus-within:text-emerald-600">
                <i className="bx bx-envelope" />
              </span>
            </div>
          </label>

          {/* Password */}
          <label className="group mt-5 block animate-in fade-in slide-in-from-bottom-2 duration-500 delay-300 fill-mode-both">
            <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-slate-500 transition-colors group-focus-within:text-emerald-700">
              Mot de passe
            </span>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                {...register("password", { required: true, minLength: 6 })}
                onChange={handleChange}
                className="block w-full rounded-xl border border-slate-200 bg-slate-50/70 py-3 pl-11 pr-12 text-sm text-slate-700 placeholder-slate-400 transition-all duration-200 hover:border-slate-300 focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-emerald-500/10"
                placeholder="••••••••"
              />
              <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-lg text-slate-400 transition-colors group-focus-within:text-emerald-600">
                <i className="bx bx-lock-alt" />
              </span>
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 grid h-8 w-8 place-items-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                aria-label={
                  showPassword
                    ? "Masquer le mot de passe"
                    : "Afficher le mot de passe"
                }
              >
                <i
                  className={`text-lg uil ${
                    showPassword ? "uil-eye" : "uil-eye-slash"
                  }`}
                />
              </button>
            </div>
          </label>

          {/* Remember + forgot */}
          <div
            className="mt-6 flex items-center justify-between animate-in fade-in duration-500 fill-mode-both"
            style={{ animationDelay: "400ms" }}
          >
            <label className="inline-flex cursor-pointer items-center select-none">
              <input
                type="checkbox"
                className="h-4 w-4 cursor-pointer rounded border-slate-300 text-emerald-600 shadow-none focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:ring-offset-0"
              />
              <span className="ml-2 text-sm text-slate-600">
                Se souvenir de moi
              </span>
            </label>
            <a
              href="#"
              className="text-sm font-medium text-emerald-700 underline-offset-4 transition-colors hover:text-emerald-800 hover:underline"
            >
              Mot de passe oublié ?
            </a>
          </div>

          {/* CTA */}
          <button
            type="submit"
            disabled={isSigningIn}
            aria-busy={isSigningIn}
            className="group/btn relative mt-7 inline-flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-green-600 px-6 py-3.5 font-medium text-white shadow-lg shadow-green-600/25 transition-all duration-300 hover:bg-green-700 hover:shadow-xl hover:shadow-green-600/30 active:bg-green-800 focus:outline-none focus:ring-4 focus:ring-emerald-500/30 disabled:cursor-not-allowed disabled:bg-green-600/60 disabled:shadow-none disabled:hover:bg-green-600/60 animate-in fade-in slide-in-from-bottom-2 duration-500 delay-500 fill-mode-both"
          >
            {/* Sheen subtil qui traverse le bouton au hover. */}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 -left-full w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/25 to-transparent transition-all duration-700 group-hover/btn:left-full"
            />
            <span>{isSigningIn ? "Connexion…" : "Se connecter"}</span>
            <i
              className={
                isSigningIn
                  ? "bx bx-loader-alt text-xl animate-spin"
                  : "bx bx-right-arrow-alt text-xl transition-transform duration-300 group-hover/btn:translate-x-1"
              }
            />
          </button>

          {/* Légal */}
          <div
            className="mt-10 border-t border-slate-100 pt-6 animate-in fade-in duration-500 fill-mode-both"
            style={{ animationDelay: "700ms" }}
          >
            <div className="flex flex-col items-center justify-center gap-3 text-xs sm:flex-row sm:gap-4">
              <Link
                to="/privacy-policy"
                className="text-slate-400 transition-colors hover:text-emerald-700"
              >
                Politique de confidentialité
              </Link>
              <span className="hidden text-slate-200 sm:inline">·</span>
              <Link
                to="/terms-of-service"
                className="text-slate-400 transition-colors hover:text-emerald-700"
              >
                Conditions d'utilisation
              </Link>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
};

export default Login;
